// Aviso de oferta nueva con el portal abierto (PP-64; en todo el portal desde
// PP-71). Hoy el operador se entera de un pedido por un sonido, WhatsApp y
// mail; aca suena un aviso corto y el titulo de la pestana muestra cuantos
// viajes esperan accion, en cualquier seccion (OfferAlert.tsx).
//
// - El sonido se genera con Web Audio: sin archivos externos (la CSP del
//   portal no sirve audio y nada sale a otro dominio).
// - Suena una vez por oferta nueva, nunca por las que ya estaban al abrir o
//   recargar la pagina, ni al volver a la pestana (useOfferAlert).
// - El navegador solo deja sonar despues de un gesto del usuario: el primer
//   clic o tecla en la pagina habilita el audio.
// - El silencio lo recuerda el navegador (localStorage). Si el storage no
//   esta (modo privado, bloqueado), el interruptor anda igual en la sesion.
import { useCallback, useEffect, useRef, useState } from "react";
import type { ProviderMultiSiteTrip } from "@ucorp/contracts";
import { pendingTitle } from "../logic/home.js";
import { offerAlertStep } from "../logic/offer-alert.js";

export interface Chime {
  /** Suena el aviso una vez. Sin audio disponible, no hace nada. */
  play(): void;
}

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** Dos tonos cortos, ~0,4 s: se oye sin ser una alarma. */
function ring(context: AudioContext): void {
  const start = context.currentTime + 0.01;
  for (const [offset, frequency] of [
    [0, 880],
    [0.18, 1175],
  ] as const) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, start + offset);
    gain.gain.exponentialRampToValueAtTime(0.25, start + offset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.2);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start + offset);
    oscillator.stop(start + offset + 0.22);
  }
}

/**
 * Aviso con Web Audio. El contexto se crea y se reanuda en el primer gesto
 * del usuario (politica de reproduccion automatica de los navegadores).
 */
export function createWebAudioChime(): Chime {
  const Ctor = audioContextCtor();
  let context: AudioContext | undefined;
  const unlock = () => {
    if (Ctor === undefined) return;
    try {
      context ??= new Ctor();
      void context.resume().catch(() => undefined);
    } catch {
      context = undefined;
    }
  };
  if (Ctor !== undefined) {
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  }
  return {
    play() {
      if (context === undefined || context.state !== "running") return;
      try {
        ring(context);
      } catch {
        // Un fallo de audio no rompe el portal: la oferta igual esta en el Inicio y en Viajes.
      }
    },
  };
}

let shared: Chime | undefined;

/**
 * El aviso de la pagina, uno solo: el audio que habilito un gesto sigue
 * habilitado al volver a ingresar (el gesto del login llega antes de que el
 * portal se monte y no lo veria un aviso nuevo).
 */
export function pageChime(): Chime {
  shared ??= createWebAudioChime();
  return shared;
}

// La clave conserva el nombre de PP-64 ("inicio"): cambiarla perderia el
// silencio que cada operador ya eligio.
const MUTED_KEY = "ucorp.portal.inicio.aviso-silenciado";

export function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTED_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeMuted(muted: boolean): void {
  try {
    if (muted) window.localStorage.setItem(MUTED_KEY, "1");
    else window.localStorage.removeItem(MUTED_KEY);
  } catch {
    // Sin storage el silencio dura lo que la pagina abierta.
  }
}

/** Interruptor de silencio, recordado por el navegador. */
export function useMuted(): [boolean, (muted: boolean) => void] {
  const [muted, setMuted] = useState(readMuted);
  const set = useCallback((next: boolean) => {
    writeMuted(next);
    setMuted(next);
  }, []);
  return [muted, set];
}

/**
 * Suena si llego una oferta por responder del filtro que no estaba en la
 * vuelta anterior (logic/offer-alert.ts). La memoria vive mientras el aviso
 * esta montado, que es todo el portal: al abrir o recargar, lo que ya esta no
 * suena; cambiar de seccion no lo remonta.
 */
export function useOfferAlert(
  all: readonly ProviderMultiSiteTrip[] | undefined,
  scoped: readonly ProviderMultiSiteTrip[],
  muted: boolean,
  chime: Chime,
): void {
  const seen = useRef<ReadonlySet<string> | undefined>(undefined);
  // Filtro, silencio y aviso se leen al llegar ofertas nuevas; cambiarlos no
  // dispara el efecto (cambiar de planta no suena).
  const latest = useRef({ scoped, muted, chime });
  latest.current = { scoped, muted, chime };
  useEffect(() => {
    if (all === undefined) return;
    const { scoped: visible, muted: silent, chime: bell } = latest.current;
    const step = offerAlertStep(seen.current, all, visible);
    seen.current = step.seen;
    if (step.ring && !silent) bell.play();
  }, [all]);
}

/** Marca el titulo de la pestana con la cantidad pendiente y lo devuelve al salir. */
export function usePendingTitle(pending: number): void {
  const base = useRef<string | undefined>(undefined);
  useEffect(() => {
    base.current ??= document.title;
    document.title = pendingTitle(base.current, pending);
  }, [pending]);
  useEffect(
    () => () => {
      if (base.current !== undefined) document.title = base.current;
    },
    [],
  );
}
